import React, {createContext, useContext, ReactNode, SetStateAction, Dispatch, useState, useEffect} from 'react';
import {supabase} from '../utils/supabase';
import dayjs, {Dayjs} from 'dayjs';
import {parseGpxParser} from '../utils/gpxParser';

export type Marker = {
    id: string,
    lng: number,
    lat: number,
    text: string,
}
export type RaceType = {
    firstname: string;
    raceName: string;
    raceDistance: string;
    hour: number;
    minute: number;
    second: number;
    day: number;
    date: Dayjs;
    mounth: number;
    year: number;
    theme: 'dark' | 'light' | 'ultrace' |'paris';
    cartography: 'dark' | 'outdoor' | 'cali' | 'light' | 'full_black' | 'vintage' | 'full_white';
    color: string;
    textColor: string,
    hike: number;
    bip: string;
    more: string;
    withTopo: boolean;
};

interface StateContextType {
    gpxFile: File|string|null;
    setGpxFile: Dispatch<SetStateAction<File|string|null>>;
    raceData: RaceType;
    setRaceData: Dispatch<SetStateAction<RaceType>>,
    xmlParsed: Document|null;
    setXmlParsed: Dispatch<SetStateAction<Document|null>>;
    markers: Array<Marker>;
    setMarkers: Dispatch<SetStateAction<Array<Marker>>>,
}

export const StateContext = createContext<StateContextType>({
    gpxFile: null,
    setGpxFile: () => {},
    xmlParsed: null,
    setXmlParsed: () => {},
    raceData: {
        firstname: '',
        raceName: '',
        hour: 0,
        minute: 0,
        second: 0,
        raceDistance: '',
        date: dayjs(),
        day: 29,
        mounth: 11,
        year: 2024,
        theme: 'ultrace',
        cartography: 'dark',
        color: '#bea674',
        textColor: '#000000',
        hike: 0,
        bip: '1121',
        more: 'SPEND MORE TIME OUTSIDE',
        withTopo: true,
    },
    setRaceData: () => {},
    markers: [],
    setMarkers: () => {}
});



type ContextProviderProps = {
    children?: ReactNode
}

export const ContextProvider = ({ children }: ContextProviderProps) => {
    const [gpxFile, setGpxFile] = useState<File|string|null>(null);
    const [xmlParsed, setXmlParsed] = useState<Document|null>(null);
    const [markers, setMarkers] = useState<Array<Marker>>([]);
    const query = new URLSearchParams(window.location.search);
    const traceId = query.get('id');

    useEffect(() => {
        const loadData = async () => {
            const { data, error } = await supabase.from('order').select().eq('order_id', traceId).maybeSingle();
            if (!data) {
                return;
            }
            setGpxFile(data.gpx);
            const xmlGpx = await parseGpxParser(data.gpx);
            setXmlParsed(xmlGpx ?? null);
            setRaceData({
                firstname: data.user_name,
                raceName: data.name,
                hour: data.hour,
                minute: data.minute,
                second: data.second,
                raceDistance: data.distance,
                date: dayjs(),
                day: data.day,
                mounth: data.mounth,
                year: data.year,
                theme: 'ultrace',
                cartography: data.carto,
                color: data.color,
                textColor: data.text_color,
                hike: data.hike,
                bip: data.bip,
                more: data.more,
                withTopo: data.with_topo,
            });
        };

        loadData();
    }, []);

    const [raceData, setRaceData] = useState<RaceType>({
        firstname: 'EMMA RATHON',
        raceName: 'TRAIL DE LA CROIX DES CARMES',
        hour: 2,
        minute: 30,
        second: 20,
        raceDistance: '32',
        date: dayjs(),
        day: (new Date()).getDate(),
        mounth: (new Date()).getMonth()+1,
        year: (new Date()).getFullYear(),
        theme: 'ultrace',
        cartography: 'dark',
        color: '#bea674',
        textColor: '#000000',
        hike: 1250,
        bip: '57',
        more: 'CHACUN SA MONTAGNE À GRAVIR',
        withTopo: true,
    });

    return (
        <StateContext.Provider value={{ gpxFile, setGpxFile, raceData, setRaceData, xmlParsed, setXmlParsed, markers, setMarkers }}>
            {children}
        </StateContext.Provider>
    )
}

export const useStateContext = () => useContext(StateContext);
